import { Component } from "react";
import {
  Card, Row, Col, Badge, Button, Form, OverlayTrigger, Popover, Dropdown, Spinner, Alert
} from "react-bootstrap";
import Alias from "components/controls/alias";
import { GoSync, GoPencil, GoTrash, GoPlus, GoEye } from "react-icons/go";
import { LuClipboardCopy, LuClipboardPaste, LuDownload } from "react-icons/lu";
import { BsThreeDotsVertical } from "react-icons/bs";
import Clipboard from "../../controls/common/clipboard";
import ActionCard, { ActionCards } from "../../controls/common/actionCard";
import { pickConverter, Converter, resolveLayoutType } from "../workspace/converter";
import EditMessageContentModal from "../../editor/details/content";
import CreateSessionObjectModal from "./create";
import Select from "components/controls/select";
import SectionLabel from "components/controls/sectionLabel";
import ConfirmModal from "components/controls/confirmModal";
import Utils from "context/utils";
import SessionContext from "../session/context";
import { canMutateSession, MUTATION_REASON_COPY } from "../session/mutationGate";
import { sessionStatusClass } from "../session/sessionStatus";
import "./index.css";

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.05;

export default class DetailsPane extends Component {
  static contextType = SessionContext;
  constructor(props) {
    super(props);
    this.state = {
      editModalVisible: false,
      editObjectAlias: null,
      editObjectValue: null,
      createModalVisible: false,
      deleteModalVisible: false,
      deleteObjectAlias: null
    };
    this.mounted = true;
  }

  componentDidMount() {
    this.sessionListener = () => this.forceUpdate();
    if (this.context) {
      this.context.addListener(this.sessionListener);
    }
  }

  componentWillUnmount() {
    if (this.context && this.sessionListener) {
      this.context.removeListener(this.sessionListener);
    }
    this.sessionListener = null;
    this.mounted = false;
  }

  exportToClipboard() {
    const converter = pickConverter(this.props.shell, this.props.shell.shellInterface);
    const definition = converter.reactFlowToBlobhubDefinition(this.props.shell.nodes, this.props.shell.edges);
    // Merge in the layout block — same as the save path. The converter
    // rebuilds the definition from scratch so layout (type, columns,
    // row_height, etc., plus the live zoom) wouldn't otherwise make it
    // into the export.
    const persistedLayout = this.props.shell.definitionObjectBackup?.layout;
    const liveZoom = this.props.shell.zoom ?? 1;
    if (persistedLayout) {
      definition.layout = { ...persistedLayout, zoom: liveZoom };
    } else if (liveZoom !== 1) {
      definition.layout = { type: "free_form", zoom: liveZoom };
    }
    const text = JSON.stringify(definition, null, 2)
    Clipboard.to(text);
  }

  importFromClipboard() {
    Clipboard.from().then((text) => {
      let definition = null;
      try {
        definition = JSON.parse(text);
        if ( definition["components"].length < 1 ) {
          return;
        }
      } catch (error) {
        console.error(error.message);
        return;
      }

      // Layout-compatibility check. Importing a definition with a
      // different layout (or, for grid, a different cols / row_height)
      // would land widgets in nonsensical positions, so reject it
      // outright with a top-level warning.
      const currentLayoutType = resolveLayoutType(this.props.shell);
      const importedLayoutType = resolveLayoutType(definition);
      const currentLayout = this.props.shell.definitionObjectBackup?.layout || {};
      const importedLayout = definition.layout || {};
      let mismatchMessage = null;
      if (currentLayoutType !== importedLayoutType) {
        mismatchMessage =
          `Imported playground layout (${importedLayoutType}) does not match the current playground layout (${currentLayoutType}).`;
      } else if (currentLayoutType === "grid") {
        if (importedLayout.columns !== currentLayout.columns
            || importedLayout.row_height !== currentLayout.row_height) {
          mismatchMessage =
            `Imported grid configuration (columns=${importedLayout.columns}, row_height=${importedLayout.row_height}) `
            + `does not match the current playground (columns=${currentLayout.columns}, row_height=${currentLayout.row_height}).`;
        }
      }
      if (mismatchMessage) {
        this.props.shell.shellInterface.updateProgress(false, null, true, mismatchMessage);
        return;
      }

      const converter = pickConverter(this.props.shell, this.props.shell.shellInterface);
      const [nodes, edges] = converter.blobhubDefinitionToReactFlow(definition, this.props.shell.manifest);

      if (currentLayoutType !== "grid") {
        // Free-form: offset new workflow to the right of existing widgets.
        // 1. Calculate the right boundary of an existing workflow
        let rightMost = 0;
        for ( const node of this.props.shell.nodes ) {
          let currentRight = node.position.x;
          for ( const port of node.data.component["ports"] ) {
            if ( "width" == port["route"] ) {
              currentRight += port["value"]["integer"];
            }
          }
          if ( currentRight > rightMost ) {
            rightMost = currentRight;
          }
        }
        // 2. Calculate the left boundary of a newly pasted workflow
        let leftMost = 0;
        for ( const node of nodes ) {
          if ( node.position.x < leftMost ) {
            leftMost = node.position.x;
          }
        }
        // 3. Compute offset
        const offset = Math.min(leftMost - rightMost, 0) * -1 + 25;
        // 4. Apply offset
        for ( const node of nodes ) {
          node.position.x += offset;
        }
      }
      // Grid: paste positions as-is; the matching grid config guarantees
      // imported coordinates make sense in the current playground.

      // Post process notes
      for ( const node of nodes ) {
        // Regenerate workflow IDs
        const newId = Utils.generateUniqueId();
        node.id = newId;
        node.data.component.id = newId;
        if (node.layout) node.layout.i = newId;
      }

      // Register enw nodes and edges
      const mergedNodes = this.props.shell.nodes.concat(nodes);
      const mergedEdges = this.props.shell.edges.concat(edges);
      this.props.shell.workflowInterface.setNodes(mergedNodes, true);
      this.props.shell.workflowInterface.setEdges(mergedEdges, true);

      // Adopt the imported zoom (when present). Same path as the slider —
      // setZoom clamps, skips no-ops, and marks the playground modified
      // so the change is saved with the rest of the import.
      const importedZoom = definition?.layout?.zoom;
      if (typeof importedZoom === "number" && importedZoom > 0) {
        this.props.shell.shellInterface.setZoom(importedZoom);
      }
    });
  }

  openEditModal(alias, value) {
    this.setState({
      editModalVisible: true,
      editObjectAlias: alias,
      editObjectValue: Utils.deepCopy(value)
    });
  }

  closeEditModal() {
    this.setState({
      editModalVisible: false,
      editObjectAlias: null,
      editObjectValue: null
    });
  }

  openDeleteModal(alias) {
    this.setState({
      deleteModalVisible: true,
      deleteObjectAlias: alias
    });
  }

  closeDeleteModal() {
    this.setState({
      deleteModalVisible: false,
      deleteObjectAlias: null
    });
  }

  handleDeleteConfirm() {
    if (this.state.deleteObjectAlias) {
      this.context.deleteSessionObject(this.state.deleteObjectAlias);
    }
    this.closeDeleteModal();
  }

  downloadSessionObject(alias, data) {
    const filename = `${this.context.session.id}_${alias}.json`;
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const href = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }

  handleSaveSessionObject(newValue) {
    this.context.uploadSessionObject(
      this.state.editObjectAlias,
      newValue
    );
  }

  openCreateModal() {
    this.setState({
      createModalVisible: true
    });
  }

  closeCreateModal() {
    this.setState({
      createModalVisible: false
    });
  }

  handleCreateSessionObject(alias, objectType) {
    // 1. Close create modal
    this.closeCreateModal();

    // 2. Build the default envelope payload for the chosen type. Items in a
    //    thread are appended later via post_session_thread_item, and graph
    //    vertices/edges via apply_session_graph_mutations, from their widgets;
    //    the envelope itself just carries attributes + metadata.
    let defaultData;
    if ("thread" === objectType) {
      defaultData = {
        type: "thread",
        thread: {
          attributes: {
            id: Utils.generateUniqueId(),
            created_at: Utils.currentTimeUtcIso()
          },
          metadata: {}
        }
      };
    } else if ("graph" === objectType) {
      defaultData = {
        type: "graph",
        graph: {
          attributes: {
            id: Utils.generateUniqueId(),
            created_at: Utils.currentTimeUtcIso()
          },
          metadata: {}
        }
      };
    } else {
      defaultData = {
        type: "message",
        message: {
          role: "user",
          attributes: {
            id: Utils.generateUniqueId(),
            created_at: Utils.currentTimeUtcIso()
          },
          content: []
        }
      };
    }

    // 3. Open the edit modal — the modal adapts its UI to the value type
    //    (message: content + attributes + providers + metadata; thread:
    //    attributes + metadata only) and the save handler uploads via
    //    uploadSessionObject on Apply.
    this.openEditModal(alias, defaultData);
  }

  renderPlaygroundSection() {
    const definition = this.props.shell.definition;
    const definitionId = definition?.id || "—";
    const layoutType = resolveLayoutType(this.props.shell);
    const zoom = this.props.shell.zoom ?? 1;
    const canEdit = !!this.props.shell.shellInterface?.canEditRevision?.();

    const layout = this.props.shell.definitionObjectBackup?.layout || {};
    const isGrid = layoutType === "grid";
    const verticalSizing = this.props.shell?.verticalSizing ?? layout.vertical_sizing ?? "pitch";
    const referenceRows = this.props.shell?.referenceRows ?? layout.reference_rows ?? 12;
    const verticalSizingOptions = [
      { value: "pitch", label: "Fixed pitch" },
      { value: "fit", label: "Fit to viewport" },
      { value: "fixed_rows", label: "Fixed rows per screen" },
    ];
    const selectedVerticalSizingOption =
      verticalSizingOptions.find((o) => o.value === verticalSizing) || verticalSizingOptions[0];
    const fmtPair = (pair) => {
      if (!Array.isArray(pair) || pair.length < 2) return "—";
      return `${pair[0]} × ${pair[1]}`;
    };
    const helpStyle = { fontSize: "0.78rem" };

    return (
      <>
        <Card.Text as="h6">Playground</Card.Text>
        <hr />
        <SectionLabel>Basics</SectionLabel>
        <table className="properties">
          <tr>
            <td className="prop-name-column">ID</td>
            <td className="prop-value-column"><code>{definitionId}</code></td>
          </tr>
        </table>

        <SectionLabel>Layout</SectionLabel>
        <table className="properties">
          <tr>
            <td className="prop-name-column">Type</td>
            <td className="prop-value-column"><code>{layoutType}</code></td>
          </tr>
          {isGrid && (
            <>
              <tr>
                <td className="prop-name-column">Dimensions</td>
                <td className="prop-value-column">
                  {(layout.columns ?? "—") + " × " + (layout.row_height != null ? `${layout.row_height} px` : "—")}
                </td>
              </tr>
              <tr>
                <td className="prop-name-column">Margin</td>
                <td className="prop-value-column">{fmtPair(layout.margin)}</td>
              </tr>
              <tr>
                <td className="prop-name-column">Padding</td>
                <td className="prop-value-column">{fmtPair(layout.container_padding)}</td>
              </tr>
            </>
          )}
        </table>

        <Form.Group className="mb-3" style={{ marginTop: "1.25rem" }}>
          <div className="d-flex align-items-center">
            <input
              type="range"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={ZOOM_STEP}
              value={zoom}
              disabled={!canEdit}
              onChange={(e) => this.props.shell.shellInterface.setZoom(parseFloat(e.target.value))}
              style={{ flex: 1, background: "transparent", border: "none", padding: 0 }}
            />
            <span style={{ marginLeft: "8px", minWidth: "40px", textAlign: "right" }}>{Math.round(zoom * 100)}%</span>
          </div>
          <Form.Text className="text-muted" style={helpStyle}>
            <strong>Zoom</strong> — canvas zoom level.
          </Form.Text>
        </Form.Group>

        {isGrid && (
          <>
            <Form.Group className="mb-3">
              <Select
                options={verticalSizingOptions}
                value={selectedVerticalSizingOption}
                isDisabled={!canEdit}
                onChange={(option) => this.props.shell.shellInterface.setVerticalSizing(option.value)}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}              />
              <Form.Text className="text-muted" style={helpStyle}>
                <strong>Vertical sizing</strong> — how row height is derived from the viewport height.
              </Form.Text>
            </Form.Group>
            {verticalSizing === "fixed_rows" && (
              <Form.Group className="mb-3">
                <Form.Control
                  type="number"
                  size="sm"
                  min={1}
                  max={128}
                  value={referenceRows}
                  disabled={!canEdit}
                  onChange={(e) => this.props.shell.shellInterface.setReferenceRows(parseInt(e.target.value, 10) || 12)}
                />
                <Form.Text className="text-muted" style={helpStyle}>
                  <strong>Reference rows</strong> — number of rows that fill the viewport height.
                </Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </>
    );
  }

  render() {
    const canEdit = this.props.shell.shellInterface.canEditRevision();
    // New Object writes session state (uploadSessionObject), so — unlike Refresh Objects, which only
    // reads — it additionally requires the session to be open. Refresh Objects/Export/Import are
    // untouched: reads/export always work, and Import edits the playground's own definition/layout,
    // not session data. Goes through the shared canMutateSession() helper (session/mutationGate.js) so
    // this seam falls back to the same default as the four widgets when shellInterface.canMutateSession
    // itself isn't exposed, rather than the fail-closed `?.()` this used to do on its own.
    const canMutate = canMutateSession(this.props.shell.shellInterface);
    // Reason-carrying counterpart to canMutate, for the Basics "Mutations" row below — surfaces WHY
    // mutations are blocked (no write access / revision not editable / session not open) rather than
    // just whether they are.
    const mutationStatus = this.props.shell.shellInterface.getMutationStatus();
    const actionRows = [];
    if ( this.context.session && canEdit ) {
      if ( canMutate ) {
        actionRows.push(
          <ActionCard key="new-object" helper="Creates a session object and opens it for editing">
            <Button variant="success" size="sm" onClick={() => this.openCreateModal()}>
              <GoPlus /> New Object
            </Button>
          </ActionCard>
        );
      }
      actionRows.push(
        <ActionCard key="refresh-session-objects" helper="Reloads the session's objects and their contents">
          <Button variant="success" size="sm" onClick={() => {
            this.context.loadSessionContent();
          }}><GoSync />&nbsp;&nbsp;Refresh Objects</Button>
        </ActionCard>
      );
    }
    actionRows.push(
      <ActionCard key="export" helper="Copies the playground definition to the clipboard">
        <Button size="sm" variant="success" onClick={(event) => { this.exportToClipboard(); }}>
          <LuClipboardCopy /> Export
        </Button>
      </ActionCard>
    );
    if ( canEdit ) {
      actionRows.push(
        <ActionCard key="import" helper="Loads a playground definition from the clipboard">
          <Button size="sm" variant="success" onClick={(event) => { this.importFromClipboard(); }}>
            <LuClipboardPaste /> Import
          </Button>
        </ActionCard>
      );
    }

    return (
      <>
        {this.renderPlaygroundSection()}
        {this.context.session &&
          <>
            <Card.Text as="h6">Session</Card.Text>
            <hr />
            <SectionLabel>Basics</SectionLabel>
            <table className="properties">
              <tr>
                <td className="prop-name-column">ID</td>
                <td className="prop-value-column"><code>{this.context.session.id}</code></td>
              </tr>
              <tr>
                <td className="prop-name-column">Status</td>
                <td className="prop-value-column">
                  <Badge variant="secondary" className={sessionStatusClass(this.context.session.status)}>
                    {this.context.session.status}
                  </Badge>
                </td>
              </tr>
              {this.context.session.created_at &&
                <tr>
                  <td className="prop-name-column">Created</td>
                  <td className="prop-value-column">
                    {Utils.formatDateTime(this.context.session.created_at, this.props.timezone)}
                  </td>
                </tr>
              }
              {this.context.session.description &&
                <tr>
                  <td className="prop-name-column">Description</td>
                  <td className="prop-value-column">{this.context.session.description}</td>
                </tr>
              }
              <tr>
                <td className="prop-name-column">Mutations</td>
                <td className="prop-value-column">
                  {mutationStatus.allowed
                    ? "Allowed"
                    : `Blocked — ${MUTATION_REASON_COPY[mutationStatus.reason]}`}
                </td>
              </tr>
            </table>

            <SectionLabel>Objects</SectionLabel>
            <Card.Text>
              {null != this.context.sessionObjects ?
                <>
                  {0 != this.context.sessionObjects.length ?
                    <>
                      {this.context.sessionObjects.map((object, index) => {
                        const data = this.context.sessionData[object.alias];

                        return (
                          <Row style={{"height": "33px"}}>
                            <Col className="d-flex align-items-center">
                              {data ?
                                <OverlayTrigger trigger={['hover', 'focus']} placement="bottom" overlay={
                                  <Popover id={`popover-positioned-bottom`} style={{ maxWidth: "100%", width: "auto" }}>
                                    <Popover.Content style={{ fontSize: "12px" }}>
                                      <div><strong>Size:</strong> {Utils.formatBytes(JSON.stringify(data).length)}</div>
                                      {object.updatedAt &&
                                        <div><strong>Updated At:</strong> {Utils.formatCompactDateTime(object.updatedAt, this.props.timezone)}</div>
                                      }
                                    </Popover.Content>
                                  </Popover>
                                }>
                                  <Alias value={object.alias} />
                                </OverlayTrigger>
                              : <Alias value={object.alias} />
                              }
                              &nbsp;&nbsp;
                              <span className="text-muted small">{(data && data.type) || "unknown"}</span>
                            </Col>
                              <Col sm={"auto"}  className="d-flex align-items-center">
                                {this.context.loadingSessionObjects.has(object.alias) &&
                                  <Spinner animation="border" role="status" size="sm" style={{marginRight: "10px"}}>
                                    <span className="sr-only">Loading...</span>
                                  </Spinner>
                                }
                                {data &&
                                  <Dropdown className="d-inline-block playground-details-action-dropdown">
                                    {/* The menu itself is available to everyone who can see the session: its
                                        read affordances (open the object, refresh, copy, download) are safe on a
                                        read-only blob, and the writes below are individually gated on canMutate.
                                        Disabling the whole toggle used to lock viewers out of reading too. */}
                                    <Dropdown.Toggle
                                      variant="light"
                                      size="sm"
                                      className="align-middle playground-action-dropdown-toggle"
                                      style={{ marginTop: "-2px" }}
                                    >
                                      <BsThreeDotsVertical />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      {/* Same viewer either way — the modal renders read-only when the mutation
                                          gate is closed (see renderEditModal), so the label tracks that. */}
                                      { ("message" === data.type || "thread" === data.type
                                          || "graph" === data.type) &&
                                        <Dropdown.Item onClick={() => this.openEditModal(object.alias, data)}>
                                          {canMutate
                                            ? <><GoPencil/>&nbsp;&nbsp;Edit Object</>
                                            : <><GoEye/>&nbsp;&nbsp;View Object</>}
                                        </Dropdown.Item>
                                      }
                                      { canMutate &&
                                        <Dropdown.Item onClick={() => this.openDeleteModal(object.alias)}>
                                          <GoTrash/>&nbsp;&nbsp;Delete Object
                                        </Dropdown.Item>
                                      }
                                      <Dropdown.Item onClick={() => this.context.loadSessionObject(object.alias)}>
                                        <GoSync/>&nbsp;&nbsp;Refresh Object
                                      </Dropdown.Item>                                      
                                      <Dropdown.Divider />
                                      <Dropdown.Item onClick={() => Clipboard.to(JSON.stringify(data, null, 2))}>
                                        <LuClipboardCopy/>&nbsp;&nbsp;Copy Object
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => this.downloadSessionObject(object.alias, data)}>
                                        <LuDownload/>&nbsp;&nbsp;Download Object
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>
                                }
                              </Col>
                          </Row>
                        );
                      })}
                    </>
                  : <>No objects are available within the selected session.</> }
                </>
              : <Alert variant="primary" className="mb-0">Loading session objects...</Alert> }
            </Card.Text>
          </>
        }

        {!this.context.session &&
          <>
            <Card.Text as="h6">Selection</Card.Text>
            <hr />
            <Card.Text>
              Select session or widget to see its properties displayed here.
            </Card.Text>
          </>
        }

        <Card.Text as="h6">Actions</Card.Text>
        <ActionCards>
          {actionRows}
        </ActionCards>

        {this.renderEditModal()}
        {this.renderCreateModal()}
        {this.renderDeleteModal()}
      </>
    );
  }

  renderEditModal() {
    if ( !this.state.editModalVisible ) {
      return null;
    }

    // Viewers reach the same modal through "View Object" (see the objects menu), so it must render
    // read-only whenever the mutation gate is closed — not just when the revision is uneditable,
    // which is all the modal would otherwise check.
    return (
      <EditMessageContentModal
        show={this.state.editModalVisible}
        portValue={this.state.editObjectValue}
        readOnly={!canMutateSession(this.props.shell.shellInterface)}
        onSave={(newValue) => this.handleSaveSessionObject(newValue)}
        onHide={() => this.closeEditModal()}
        shell={this.props.shell}
      />
    );
  }

  renderCreateModal() {
    if ( !this.state.createModalVisible ) {
      return null;
    }

    const existingAliases = this.context.sessionObjects 
      ? this.context.sessionObjects.map(obj => obj.alias) 
      : [];

    return (
      <CreateSessionObjectModal
        show={this.state.createModalVisible}
        existingAliases={existingAliases}
        onSave={(alias, objectType) => this.handleCreateSessionObject(alias, objectType)}
        onHide={() => this.closeCreateModal()}
      />
    );
  }

  renderDeleteModal() {
    if (!this.state.deleteModalVisible || !this.state.deleteObjectAlias) {
      return null;
    }

    return (
      <ConfirmModal
        show={this.state.deleteModalVisible}
        onHide={() => this.closeDeleteModal()}
        onConfirm={() => this.handleDeleteConfirm()}
        title="Delete Session Object"
        body={
          <>
            Are you sure you want to delete session object <strong>{this.state.deleteObjectAlias}</strong>?
          </>
        }
      />
    );
  }
}
